/**
 * Hero progression: the XP curve, the level cap, and what a level grants
 * (PRD #792).
 *
 * The Hero record has carried a `level` and an `xp` since the tracer, and
 * `settleDungeonRun` has banked XP after every run, but nothing ever
 * incremented the level. This module is the arithmetic that closes that loop.
 * It is pure: no world, no socket, no account record, so the server can settle
 * a run against it and a test can walk the whole curve without either.
 *
 * **Every constant here is first-pass.** The curve, the cap, the points per
 * level, and the respec price are declared once so a tuning change has one
 * place to happen, and their values are dialed by the tuning PRD (#799)
 * against a balance harness that does not exist yet. There is one placeholder
 * monster to measure against, so any number defended here would be defending a
 * placeholder.
 */

/** The highest level this milestone grants. Content beyond it is later work. */
export const HERO_LEVEL_CAP = 20;

/**
 * XP to advance one level, multiplied by the level being left.
 *
 * Level 1 to 2 costs 100, level 2 to 3 costs 200, and so on, which makes the
 * cumulative requirement `50 * level * (level - 1)`: 100 XP for level 2, and
 * 19,000 for the cap.
 */
export const HERO_XP_PER_LEVEL_STEP = 100;

/** Attribute points a level grants. Class-independent in this milestone. */
export const HERO_ATTRIBUTE_POINTS_PER_LEVEL = 5;

/** What a respec costs, drawn from the account wallet. A pure sink: the mode sells nothing. */
export const HERO_RESPEC_GOLD_COST = 500;

/**
 * Cumulative XP required to be this level.
 *
 * Level 1 requires nothing, which is what makes a fresh Hero valid before it
 * has killed anything. A level above the cap answers the cap's requirement, so
 * a caller cannot accidentally demand XP for a level that cannot be reached.
 */
export function xpForLevel(level: number): number {
  if (!Number.isFinite(level)) return 0;
  const bounded = Math.min(HERO_LEVEL_CAP, Math.max(1, Math.floor(level)));
  return (HERO_XP_PER_LEVEL_STEP / 2) * bounded * (bounded - 1);
}

/**
 * The level a banked XP total earns, clamped to the cap.
 *
 * Walked rather than solved, because the closed form needs a square root and a
 * float rounding error at a threshold would hand out a level early or late.
 * The cap is twenty, so the walk is bounded and cheap.
 */
export function levelForXp(xp: number): number {
  if (!Number.isFinite(xp) || xp <= 0) return 1;
  let level = 1;
  while (level < HERO_LEVEL_CAP && xp >= xpForLevel(level + 1)) level += 1;
  return level;
}

/** XP still to earn before the next level, or zero at the cap. */
export function xpToNextLevel(xp: number): number {
  const level = levelForXp(xp);
  if (level >= HERO_LEVEL_CAP) return 0;
  return Math.max(0, xpForLevel(level + 1) - Math.max(0, Math.floor(xp)));
}

/**
 * Attribute points a Hero of this level has been granted in total.
 *
 * Level 1 grants none: the first allocation decision is the reward for the
 * first level, not a choice made before the Hero has swung at anything.
 */
export function attributePointsForLevel(level: number): number {
  if (!Number.isFinite(level)) return 0;
  const bounded = Math.min(HERO_LEVEL_CAP, Math.max(1, Math.floor(level)));
  return (bounded - 1) * HERO_ATTRIBUTE_POINTS_PER_LEVEL;
}
